import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Search, Briefcase, Building, MapPin, 
  Clock, Filter, Plus, ChevronDown,
  GraduationCap, 
   Wallet,  Send, ChartBar
} from 'lucide-react';
import COMPANY_LOGOS from '../constants/companyLogos';
import JobAnalyticsModal from '../components/JobAnalyticsModal';
import HeroSection from '../components/HeroSection';

const EXPERIENCE_RANGES = [
  { label: '0-2 years', min: 0, max: 2 },
  { label: '2-5 years', min: 2, max: 5 },
  { label: '5-8 years', min: 5, max: 8 },
  { label: '8-12 years', min: 8, max: 12 },
  { label: '12+ years', min: 12, max: 100 }
];

const SALARY_RANGES = [
  { label: '3-6 LPA', min: 300000, max: 600000 },
  { label: '6-10 LPA', min: 600000, max: 1000000 },
  { label: '10-15 LPA', min: 1000000, max: 1500000 },
  { label: '15-25 LPA', min: 1500000, max: 2500000 },
  { label: '25-50 LPA', min: 2500000, max: 5000000 },
  { label: '50+ LPA', min: 5000000, max: Infinity }
];
// Utility Functions
const formatSalary = (min, max, isNegotiable) => {
  if (!min && !max) return 'Not specified';
  if (isNegotiable) return 'Negotiable';
  if (min && max) return `₹${formatCurrency(min)} - ₹${formatCurrency(max)}`;
  if (min) return `From ₹${formatCurrency(min)}`;
  return `Up to ₹${formatCurrency(max)}`;
};

const formatCurrency = (amount) => {
  if (amount >= 100000) {
    return `${(amount / 100000).toFixed(1)}L`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(1)}K`;
  }
  return amount.toString();
};



const getTimeAgo = (date) => {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1
  };

  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInUnit);
    if (interval > 1) {
      return `${interval} ${unit}s ago`;
    }
    if (interval === 1) {
      return `1 ${unit} ago`;
    }
  }
  return 'Just now';
};


// Modify the RangeFilter component to accept predefined ranges
const RangeFilter = ({ value, onChange, options, label }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full py-2.5 pl-3 pr-10 text-gray-900 bg-white border border-gray-300 
        rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent
        cursor-pointer transition-all duration-200"
    >
      <option value="">All Ranges</option>
      {options.map((range) => (
        <option key={range.label} value={range.label}>
          {range.label}
        </option>
      ))}
    </select>
  </div>
);
const FilterSection = ({ label, children }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-700">
      {label}
    </label>
    {children}
  </div>
);

const SelectFilter = ({ value, onChange, options, placeholder }) => (
  <select
    className="w-full py-2.5 pl-3 pr-10 text-gray-900 bg-white border border-gray-300 
      rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent
      cursor-pointer transition-all duration-200"
    value={value}
    onChange={onChange}
  >
    <option value="">{placeholder}</option>
    {Array.isArray(options) && options.map((opt) => {
      // Handle both simple strings and object options
      const optValue = typeof opt === 'object' ? opt.value : opt;
      const optLabel = typeof opt === 'object' ? opt.label : opt;
      return (
        <option key={optValue} value={optValue}>
          {optLabel}
        </option>
      );
    })}
  </select>
);



const Stat = ({ icon: Icon, value, label, className }) => (
  <div className={`bg-white rounded-2xl shadow-sm p-6 flex items-center space-x-4 hover:shadow-md transition-shadow ${className}`}>
    <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
      <Icon className="h-6 w-6 text-blue-600" />
    </div>
    <div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  </div>
);

const JobCard = ({ job, onClick }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 
        cursor-pointer group border border-gray-100 p-6"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="h-12 w-12 rounded-xl bg-gray-50 flex items-center justify-center p-2">
            {COMPANY_LOGOS[job.company.name] ? (
              <img
                src={COMPANY_LOGOS[job.company.name]}
                alt={job.company.name}
                className="h-full w-full object-contain"
              />
            ) : (
              <Building className="h-6 w-6 text-gray-400" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 line-clamp-1">
              {job.jobDetails.title}
            </h3>
            <p className="text-sm text-gray-600">{job.company.name}</p>
          </div>
        </div>
        <div className="flex flex-col items-end space-y-2">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs 
            font-medium ${
              new Date(job.applicationDetails.deadline) > new Date() 
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {new Date(job.applicationDetails.deadline) > new Date() ? 'Active' : 'Closed'}
          </span>
          {job.referralEnabled && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs 
              font-medium bg-purple-100 text-purple-800">
              Referral
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-3">
        <div className="flex items-center">
          <MapPin className="h-4 w-4 mr-1" />
          {job.company.location}
        </div>
        <div className="flex items-center">
          <Briefcase className="h-4 w-4 mr-1" />
          {job.jobDetails.type}
        </div>
        <div className="flex items-center">
          <GraduationCap className="h-4 w-4 mr-1" />
          {`${job.jobDetails.experience.min}-${job.jobDetails.experience.max} yrs`}
        </div>
        <div className="flex items-center font-medium text-gray-900">
          <Wallet className="h-4 w-4 mr-1" />
          {formatSalary(job.jobDetails.salary.min, job.jobDetails.salary.max, job.jobDetails.salary.isNegotiable)}
        </div>
      </div>

      {job.jobDetails.skills.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {job.jobDetails.skills.slice(0, 3).map((skill, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs 
                font-medium bg-blue-50 text-blue-700"
            >
              {skill}
            </span>
          ))}
          {job.jobDetails.skills.length > 3 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs 
              font-medium bg-gray-100 text-gray-600">
              +{job.jobDetails.skills.length - 3} more
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
        <div className="text-sm text-gray-500">
          {getTimeAgo(job.createdAt)}
        </div>
        <div className="flex items-center space-x-3">
          <button
            
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium 
              text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
          >
            Apply Now
          </button>
        </div>
      </div>
    </motion.div>
  );
};


const JobList = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [sortBy, setSortBy] = useState('latest');
  const [filters, setFilters] = useState({
    search: '',
    jobType: '',
    experienceRange: '',
    salaryRange: '',
    location: '',
    skills: [],
    qualification: '',
    referralEnabled: '',
    postedWithin: '',
    jobStatus: 'active'  // Add this line with default value 'active'
  });
  const JOB_STATUS_OPTIONS = [
    { value: 'active', label: 'Active Jobs Only' },
    { value: 'closed', label: 'Closed Jobs Only' },
    { value: 'all', label: 'All Jobs' }
  ];
  const postedWithinOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: 'all', label: 'All time' }
  ];

  const referralOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' }
  ];

  const applyFilters = useCallback(() => {
    let filtered = [...jobs];
  
    // Job Status filter
    if (filters.jobStatus !== 'all') {
      filtered = filtered.filter(job => {
        const isActive = new Date(job.applicationDetails.deadline) > new Date();
        return filters.jobStatus === 'active' ? isActive : !isActive;
      });
    }
  
    // Existing filters remain the same...
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(job => 
        job.jobDetails.title.toLowerCase().includes(searchLower) ||
        job.company.name.toLowerCase().includes(searchLower) ||
        job.company.location.toLowerCase().includes(searchLower) ||
        job.jobDetails.skills.some(skill => 
          skill.toLowerCase().includes(searchLower)
        )
      );
    }
  

    // Job Type filter
    if (filters.jobType) {
      filtered = filtered.filter(job => 
        job.jobDetails.type === filters.jobType
      );
    }

    // Experience Range filter
    if (filters.experienceRange) {
      const selectedRange = EXPERIENCE_RANGES.find(range => range.label === filters.experienceRange);
      if (selectedRange) {
        filtered = filtered.filter(job => {
          // Check if job's range overlaps with selected range
          return (job.jobDetails.experience.min <= selectedRange.max && 
                 job.jobDetails.experience.max >= selectedRange.min);
        });
      }
    }


    // Salary Range filter
    if (filters.salaryRange) {
      const selectedRange = SALARY_RANGES.find(range => range.label === filters.salaryRange);
      if (selectedRange) {
        filtered = filtered.filter(job => {
          // Check if job's salary range overlaps with selected range
          return (job.jobDetails.salary.min <= selectedRange.max && 
                 job.jobDetails.salary.max >= selectedRange.min);
        });
      }
    }

    // Location filter
    if (filters.location) {
      filtered = filtered.filter(job => 
        job.company.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Skills filter
    if (filters.skills.length > 0) {
      filtered = filtered.filter(job => 
        filters.skills.every(skill => 
          job.jobDetails.skills.map(s => s.toLowerCase())
            .includes(skill.toLowerCase())
        )
      );
    }

    // Qualification filter
    if (filters.qualification) {
      filtered = filtered.filter(job => 
        job.jobDetails.qualifications.includes(filters.qualification)
      );
    }

    // Referral filter
    if (filters.referralEnabled) {
      filtered = filtered.filter(job => 
        job.referralEnabled === (filters.referralEnabled === 'yes')
      );
    }

    // Posted Within filter
    if (filters.postedWithin) {
      const now = new Date();
      const days = {
        '7d': 7,
        '30d': 30
      }[filters.postedWithin];

      if (days) {
        const cutoff = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
        filtered = filtered.filter(job => 
          new Date(job.createdAt) >= cutoff
        );
      }
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'latest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'salary':
          const avgSalaryA = (a.jobDetails.salary.min + a.jobDetails.salary.max) / 2;
          const avgSalaryB = (b.jobDetails.salary.min + b.jobDetails.salary.max) / 2;
          return avgSalaryB - avgSalaryA;
        case 'deadline':
          return new Date(a.applicationDetails.deadline) - new Date(b.applicationDetails.deadline);
        default:
          return 0;
      }
    });

    return filtered;
  }, [jobs, filters, sortBy]);

  // Calculate statistics from jobs data
  const getStatistics = useCallback(() => {
    const activeJobs = jobs.filter(job => {
      const deadline = new Date(job.applicationDetails.deadline);
      return deadline > new Date();
    });

    const totalCompanies = [...new Set(jobs.map(job => job.company.name))].length;
    const totalLocations = [...new Set(jobs.map(job => job.company.location))].length;
    const avgSalary = jobs.reduce((acc, job) => {
      const avg = (job.jobDetails.salary.min + job.jobDetails.salary.max) / 2;
      return acc + avg;
    }, 0) / jobs.length;

    return {
      totalJobs: jobs.length,
      activeJobs: activeJobs.length,
      totalCompanies,
      totalLocations,
      avgSalary: Math.round(avgSalary),
      referralJobs: jobs.filter(job => job.referralEnabled).length
    };
  }, [jobs]);

  const fetchJobs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/jobs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch jobs');
      const data = await response.json();
      setJobs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    const filtered = applyFilters();
    setFilteredJobs(filtered);
  }, [applyFilters]);

  // Get unique values for filters
  const locations = [...new Set(jobs.map(job => job.company.location))];
  const stats = getStatistics();

  // Pagination
  const indexOfLastJob = currentPage * itemsPerPage;
  const indexOfFirstJob = indexOfLastJob - itemsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-blue-500 border-t-transparent" />
          <p className="text-gray-600">Loading opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}

      <HeroSection
  title="Job Opportunities"
  subtitle={`Explore ${stats.activeJobs} active opportunities across ${stats.totalCompanies} companies`}
  showSearch={true}
  searchConfig={{
    value: filters.search,
    onChange: (e) => setFilters({ ...filters, search: e.target.value }),
    placeholder: "Search jobs by title, company, skills..."
  }}
  statsConfig={[
    { value: stats.activeJobs, label: "Active Jobs" },
    { value: stats.totalCompanies, label: "Companies Hiring" },
    { value: stats.totalLocations, label: "Locations" }
  ]}
  analyticsComponent={<JobAnalyticsModal jobs={jobs} />}
/>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
       

        {/* Filters Section */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8 mt-16">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          >
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Jobs</h2>
            </div>
            <div className="flex items-center space-x-4">
              {isFiltersExpanded && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFilters({
                      search: '',
                      jobType: '',
                      experienceRange: ['', ''],
                      salaryRange: ['', ''],
                      location: '',
                      skills: [],
                      qualification: '',
                      referralEnabled: '',
                      postedWithin: '',
                      jobStatus: 'active'
                      });
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear all filters
                </button>
              )}
              <ChevronDown 
                className={`h-5 w-5 text-gray-400 transition-transform duration-200
                  ${isFiltersExpanded ? 'transform rotate-180' : ''}`}
              />
            </div>
          </div>

          <motion.div
            initial={false}
            animate={{
              height: isFiltersExpanded ? 'auto' : 0,
              opacity: isFiltersExpanded ? 1 : 0
            }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
        <FilterSection label="Job Type">
          <SelectFilter
            value={filters.jobType}
            onChange={(e) => setFilters({ ...filters, jobType: e.target.value })}
            options={['full-time', 'part-time', 'contract', 'internship']}
            placeholder="All Types"
          />
        </FilterSection>
        <FilterSection label="Job Status">
  <SelectFilter
    value={filters.jobStatus}
    onChange={(e) => setFilters({ ...filters, jobStatus: e.target.value })}
    options={JOB_STATUS_OPTIONS}
    placeholder="Select Status"
  />
</FilterSection>
        <FilterSection label="Experience (yrs)">
        <RangeFilter
          value={filters.experienceRange}
          onChange={(value) => setFilters({ ...filters, experienceRange: value })}
          options={EXPERIENCE_RANGES}
        />
      </FilterSection>

      <FilterSection label="Salary Range (annual ctc)">
        <RangeFilter
          value={filters.salaryRange}
          onChange={(value) => setFilters({ ...filters, salaryRange: value })}
          options={SALARY_RANGES}
        />
      </FilterSection>

        <FilterSection label="Location">
          <SelectFilter
            value={filters.location}
            onChange={(e) => setFilters({ ...filters, location: e.target.value })}
            options={locations}
            placeholder="All Locations"
          />
        </FilterSection>

        <FilterSection label="Posted Within">
          <SelectFilter
            value={filters.postedWithin}
            onChange={(e) => setFilters({ ...filters, postedWithin: e.target.value })}
            options={postedWithinOptions}
            placeholder="Any time"
          />
        </FilterSection>

        <FilterSection label="Referral Available">
          <SelectFilter
            value={filters.referralEnabled}
            onChange={(e) => setFilters({ ...filters, referralEnabled: e.target.value })}
            options={referralOptions}
            placeholder="All Jobs"
          />
        </FilterSection>
      </div>
          </motion.div>
        </div>

        {/* Results and Sort */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-600">
            Showing {filteredJobs.length} jobs
          </p>

          <div className="flex items-center space-x-4">
            <label className="text-sm text-gray-600">Sort by</label>
            <select
              className="text-sm border border-gray-300 rounded-lg px-3 py-2
                focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="latest">Most Recent</option>
              <option value="salary">Highest Salary</option>
              <option value="deadline">Deadline</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
  {currentJobs.map(job => (
    <JobCard
      key={job._id}
      job={job}
      onClick={() => navigate(`/jobs/${job._id}`)}
    />
  ))}
</div>

        {/* Empty State */}
        {filteredJobs.length === 0 && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
              <Search className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No jobs found
            </h3>
            <p className="text-gray-500">
              Try adjusting your filters or search terms
            </p>
          </div>
        )}

        {/* Pagination */}
        {filteredJobs.length > 0 && (
          <div className="flex items-center justify-center space-x-2 mb-12">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Previous
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-10 h-10 rounded-lg ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            {totalPages > 5 && (
              <>
                <span className="px-2">...</span>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className={`w-10 h-10 rounded-lg ${
                    currentPage === totalPages
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {totalPages}
                </button>
              </>
            )}

            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Next
            </button>
          </div>
        )}

        {/* Recent Jobs Section */}
        <div className="border-t border-gray-200 pt-12 mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Recently Posted Jobs</h2>
            <Clock className="h-5 w-5 text-gray-400" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .slice(0, 3)
              .map(job => (
                <div
                  key={job._id}
                  className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow 
                    cursor-pointer border border-gray-100"
                  onClick={() => navigate(`/jobs/${job._id}`)}
                >
                  <div className="flex items-center space-x-3">
                    <div className="h-12 w-12 rounded-lg bg-gray-50 flex items-center justify-center">
                      {COMPANY_LOGOS[job.company.name] ? (
                        <img
                          src={COMPANY_LOGOS[job.company.name]}
                          alt={job.company.name}
                          className="h-8 w-8 object-contain"
                        />
                      ) : (
                        <Building className="h-6 w-6 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {job.jobDetails.title}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {job.company.name}
                      </p>
                      <div className="mt-1 flex items-center text-xs text-gray-500">
                        <MapPin className="h-3 w-3 mr-1" />
                        {job.company.location}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {getTimeAgo(job.createdAt)}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Quick Stats Section */}
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {Math.round((stats.referralJobs / stats.totalJobs) * 100)}%
              </div>
              <div className="mt-2 text-sm text-gray-600">
                Jobs with Referral Program
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                ₹{formatCurrency(stats.avgSalary)}
              </div>
              <div className="mt-2 text-sm text-gray-600">
                Average Annual CTC
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {stats.totalLocations}+
              </div>
              <div className="mt-2 text-sm text-gray-600">
                Locations Hiring
              </div>
            </div>
          </div>
        </div>

        {/* Action Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 mb-12">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-white mb-4 md:mb-0">
              <h3 className="text-xl font-semibold mb-2">
                Want to post a job opportunity?
              </h3>
              <p className="text-blue-100">
                Share opportunities and connect with talented alumni
              </p>
            </div>
            <button
              onClick={() => navigate('/jobs/create')}
              className="inline-flex items-center px-6 py-3 border border-transparent 
                text-base font-medium rounded-xl text-blue-700 bg-white 
                hover:bg-blue-50 transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              Post a Job
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobList;